import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import StarIcon from "@mui/icons-material/Star";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";

import { motion } from "framer-motion";
import ApiServices from "../../Utils/ApiServices";

const MotionCard = motion(Card);
const MotionGridItem = motion(Grid);

const getImageUrl = (image) => {
  if (!image) return "";
  if (String(image).startsWith("http://") || String(image).startsWith("https://")) return image;
  return `https://riteseo.com${String(image).startsWith("/") ? image : `/${image}`}`;
};

const money = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "—";
  return `$${Number(value).toFixed(2)}`;
};


const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.04,
      duration: 0.45,
      ease: [0.22, 1, 0.36, 1],
    },
  }),
};

const getOfferPrice = (offer, serviceType) => {
  if (!offer) return null;

  if (offer?.pricing?.[serviceType] !== undefined && offer?.pricing?.[serviceType] !== null) {
    return Number(offer.pricing[serviceType]);
  }

  if (offer?.calculated_prices?.[serviceType]?.final_price !== undefined) {
    return Number(offer.calculated_prices[serviceType].final_price);
  }

  return null;
};

const getOfferOriginalPrice = (offer, serviceType) => {
  if (!offer) return null;

  if (
    offer?.variant_details?.pricing?.[serviceType] !== undefined &&
    offer?.variant_details?.pricing?.[serviceType] !== null
  ) {
    return Number(offer.variant_details.pricing[serviceType]);
  }

  if (
    offer?.item_details?.pricing?.[serviceType] !== undefined &&
    offer?.item_details?.pricing?.[serviceType] !== null
  ) {
    return Number(offer.item_details.pricing[serviceType]);
  }

  if (offer?.calculated_prices?.[serviceType]?.subtotal !== undefined) {
    return Number(offer.calculated_prices[serviceType].subtotal);
  }

  return null;
};

const getErrorText = (error, fallback) =>
  error?.response?.data?.detail ||
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  fallback;

const getSpecialCartPayload = (special, quantity, notes) => {
  const itemId = special?.item || special?.item_id || special?.item_details?.id;
  const variantId =
    special?.variant || special?.variant_id || special?.variant_details?.id || null;

  return {
    item_id: itemId,
    variant_id: variantId || null,
    quantity: Number(quantity || 1),
    notes: notes || "",
  };
};

const MenuOfferSections = ({
  activeSpecials,
  activeCombos,
  loading,
  brand,
  serviceType,
  customerId,
  token,
  isLoggedIn,
  selectedBranchId,
  refreshCart,
  showMessage,
}) => {
  const [offerDialog, setOfferDialog] = useState({
    open: false,
    type: "",
    data: null,
    quantity: 1,
    notes: "",
  });

  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  const openOfferDialog = (type, data) => {
    setOfferDialog({ open: true, type, data, quantity: 1, notes: "" });
  };

  const closeOfferDialog = () => {
    setOfferDialog({ open: false, type: "", data: null, quantity: 1, notes: "" });
  };

  const updateCartMeta = async () => {
    const cartId = localStorage.getItem("cart_id");
    if (!cartId || !customerId) return;

    try {
      await ApiServices.patch(
        `/api/v1/customers/${customerId}/cart/${cartId}/`,
        { service_type: serviceType, location_id: selectedBranchId },
        authHeaders
      );
    } catch {}
  };

  const addSpecialToCart = async () => {
    if (!isLoggedIn) {
      showMessage("Please login or signup to add specials to cart.", "warning");
      return;
    }

    if (!customerId) {
      showMessage("Customer id not found in session storage.", "error");
      return;
    }

    const special = offerDialog.data;
    if (!special) return;

    const payload = getSpecialCartPayload(
      special,
      offerDialog.quantity,
      offerDialog.notes
    );

    if (!payload.item_id) {
      showMessage("Special item id is missing.", "error");
      return;
    }

    try {
      await ApiServices.post(
        `/api/v1/customers/${customerId}/cart/items/`,
        payload,
        authHeaders
      );

      // await updateCartMeta();
      await refreshCart();
      closeOfferDialog();
      showMessage("Special added to cart.", "success");
    } catch (error) {
      showMessage(getErrorText(error, "Failed to add special to cart."), "error");
    }
  };

  const addComboToCart = async () => {
    if (!isLoggedIn) {
      showMessage("Please login or signup to add combos to cart.", "warning");
      return;
    }

    if (!customerId) {
      showMessage("Customer id not found in session storage.", "error");
      return;
    }

    const combo = offerDialog.data;
    if (!combo?.id) return;

    try {
      await ApiServices.post(
        `/api/v1/customers/${customerId}/cart/combos/`,
        {
          combo_id: combo.id,
          quantity: Number(offerDialog.quantity || 1),
          notes: offerDialog.notes || "",
        },
        authHeaders
      );

      // await updateCartMeta();
      await refreshCart();
      closeOfferDialog();
      showMessage("Combo added to cart.", "success");
    } catch (error) {
      showMessage(getErrorText(error, "Failed to add combo to cart."), "error");
    }
  };

  const confirmOfferAddToCart = () => {
    if (offerDialog.type === "special") return addSpecialToCart();
    if (offerDialog.type === "combo") return addComboToCart();
    return null;
  };

  const renderSpecialCard = (special, index) => {
    const imageUrl =
      getImageUrl(special.image_url || special.image || special.item_details?.image) ||
      "https://images.unsplash.com/photo-1600891964599-f61ba0e24092?q=80&w=1200&auto=format&fit=crop";
    const specialPrice = getOfferPrice(special, serviceType);
    const regularPrice = getOfferOriginalPrice(special, serviceType);

    return (
      <MotionGridItem
        item
        xs={12}
        sm={6}
        lg={4}
        key={special.id}
        custom={index}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.1 }}
        variants={cardVariants}
      >
        <motion.div whileHover={{ y: -8, scale: 1.01 }}>
          <Card
            sx={{
              height: "100%",
              borderRadius: 5,
              overflow: "hidden",
              background: "linear-gradient(180deg, #fff 0%, #FFF8F0 100%)",
              border: "1px solid rgba(230,74,25,0.18)",
              boxShadow: "0 16px 44px rgba(230,74,25,0.14)",
            }}
          >
            <Box sx={{ position: "relative", height: 210, overflow: "hidden" }}>
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  backgroundImage: `url("${imageUrl}")`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "linear-gradient(180deg, rgba(0,0,0,0.05) 25%, rgba(42,10,10,0.72) 100%)",
                }}
              />
              <Chip
                size="small"
                label={special.badge?.text || "Today's Special"}
                sx={{
                  position: "absolute",
                  top: 14,
                  left: 14,
                  bgcolor: special.badge?.color || brand.orange,
                  color: "#fff",
                  fontWeight: 900,
                }}
              />
              {special.active_now !== false && (
                <Chip
                  size="small"
                  label="Active Now"
                  sx={{
                    position: "absolute",
                    top: 14,
                    right: 14,
                    bgcolor: "rgba(255,255,255,0.95)",
                    color: brand.redDeep,
                    fontWeight: 900,
                  }}
                />
              )}
              <Box sx={{ position: "absolute", left: 16, right: 16, bottom: 14, color: "#fff" }}>
                <Typography
                  sx={{
                    fontFamily: "'Playfair Display', Georgia, serif",
                    fontWeight: 900,
                    fontSize: 24,
                    lineHeight: 1.05,
                  }}
                >
                  {special.title}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.92 }}>
                  {special.item_name}
                  {special.variant_name ? ` · ${special.variant_name}` : ""}
                </Typography>
              </Box>
            </Box>

            <CardContent sx={{ p: 2.5 }}>
              <Stack spacing={1.5}>
                <Typography variant="body2" color="text.secondary" sx={{ minHeight: 38 }}>
                  {special.description ||
                    special.item_details?.description ||
                    "Limited-time special offer."}
                </Typography>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip size="small" label={special.item_details?.is_veg ? "Veg" : "Non-Veg"} />
                  <Chip
                    size="small"
                    icon={<AccessTimeIcon sx={{ fontSize: "16px !important" }} />}
                    label={`${special.item_details?.prep_time_minutes || 0} min`}
                  />
                  <Chip
                    size="small"
                    icon={<StarIcon sx={{ fontSize: "16px !important", color: "#FFB300 !important" }} />}
                    label={`${special.item_details?.rating || "0"}`}
                  />
                </Stack>

                <Divider sx={{ borderStyle: "dashed" }} />

                <Stack direction="row" justifyContent="space-between" alignItems="end">
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 1 }}>
                      SPECIAL PRICE
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="baseline">
                      <Typography sx={{ fontWeight: 900, color: brand.red, fontSize: 24 }}>
                        {money(specialPrice)}
                      </Typography>
                      {regularPrice !== null && (
                        <Typography
                          sx={{
                            textDecoration: "line-through",
                            color: "text.secondary",
                            fontSize: 13,
                          }}
                        >
                          {money(regularPrice)}
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<AddShoppingCartIcon />}
                    onClick={() => openOfferDialog("special", special)}
                    sx={{
                      borderRadius: 999,
                      fontWeight: 800,
                      textTransform: "none",
                      background: brand.gradientPrimary,
                    }}
                  >
                    Add
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </motion.div>
      </MotionGridItem>
    );
  };

  const renderComboCard = (combo, index) => {
    const imageUrl =
      getImageUrl(combo.image_url || combo.image) ||
      "https://images.unsplash.com/photo-1550547660-d9450f859349?q=80&w=1200&auto=format&fit=crop";
    const price = combo.calculated_prices?.[serviceType];

    return (
      <MotionGridItem
        item
        xs={12}
        sm={6}
        lg={4}
        key={combo.id}
        custom={index}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.1 }}
        variants={cardVariants}
      >
        <motion.div whileHover={{ y: -8, scale: 1.01 }}>
          <Card
            sx={{
              height: "100%",
              borderRadius: 5,
              overflow: "hidden",
              background: "linear-gradient(180deg, #fff 0%, #FFF8F0 100%)",
              border: "1px solid rgba(183,28,28,0.12)",
              boxShadow: "0 16px 44px rgba(122,14,14,0.12)",
            }}
          >
            <Box sx={{ position: "relative", height: 210, overflow: "hidden" }}>
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  backgroundImage: `url("${imageUrl}")`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "linear-gradient(180deg, rgba(0,0,0,0.04) 35%, rgba(42,10,10,0.7) 100%)",
                }}
              />
              <Chip
                size="small"
                label="Combo Meal"
                sx={{
                  position: "absolute",
                  top: 14,
                  left: 14,
                  background: brand.gradientAmber,
                  color: brand.redDeep,
                  fontWeight: 900,
                }}
              />
              <Box sx={{ position: "absolute", left: 16, right: 16, bottom: 14, color: "#fff" }}>
                <Typography
                  sx={{
                    fontFamily: "'Playfair Display', Georgia, serif",
                    fontWeight: 900,
                    fontSize: 24,
                    lineHeight: 1.05,
                  }}
                >
                  {combo.name}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.92 }}>
                  {(combo.combo_items || []).length} items included
                </Typography>
              </Box>
            </Box>

            <CardContent sx={{ p: 2.5 }}>
              <Stack spacing={1.5}>
                <Typography variant="body2" color="text.secondary" sx={{ minHeight: 38 }}>
                  {combo.description || "A complete meal combo at a special price."}
                </Typography>

                <Stack spacing={0.8}>
                  {(combo.combo_items || []).slice(0, 4).map((item) => (
                    <Stack
                      key={item.id || `${item.item_id}-${item.display_order}`}
                      direction="row"
                      justifyContent="space-between"
                      spacing={1}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {item.quantity}× {item.item_name}
                        {item.variant_name ? ` (${item.variant_name})` : ""}
                      </Typography>
                      {item.is_free && <Chip size="small" label="Free" color="success" />}
                    </Stack>
                  ))}
                </Stack>

                <Divider sx={{ borderStyle: "dashed" }} />

                <Stack direction="row" justifyContent="space-between" alignItems="end">
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 1 }}>
                      COMBO PRICE
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="baseline">
                      <Typography sx={{ fontWeight: 900, color: brand.red, fontSize: 24 }}>
                        {money(price?.final_price)}
                      </Typography>
                      {price?.subtotal && (
                        <Typography
                          sx={{
                            textDecoration: "line-through",
                            color: "text.secondary",
                            fontSize: 13,
                          }}
                        >
                          {money(price.subtotal)}
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<AddShoppingCartIcon />}
                    onClick={() => openOfferDialog("combo", combo)}
                    sx={{
                      borderRadius: 999,
                      fontWeight: 800,
                      textTransform: "none",
                      background: brand.gradientPrimary,
                    }}
                  >
                    Add
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </motion.div>
      </MotionGridItem>
    );
  };

  if (loading) return null;

  return (
    <>
      {!!activeSpecials.length && (
        <MotionCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          sx={{
            borderRadius: 5,
            mb: 4,
            border: "1px solid rgba(230,74,25,0.14)",
            boxShadow: "0 14px 36px rgba(230,74,25,0.10)",
            background: "linear-gradient(180deg, #ffffff 0%, #FFF7ED 100%)",
          }}
        >
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "50%",
                  display: "grid",
                  placeItems: "center",
                  background: brand.gradientAmber,
                }}
              >
                <LocalFireDepartmentIcon sx={{ color: brand.redDeep }} />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontFamily: "'Playfair Display', Georgia, serif",
                    fontWeight: 900,
                    fontSize: { xs: 24, md: 30 },
                    color: brand.charcoal,
                  }}
                >
                  Today's Active Specials
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Limited-time offers available for this branch.
                </Typography>
              </Box>
            </Stack>
            <Grid container spacing={3}>
              {activeSpecials.map((special, index) => renderSpecialCard(special, index))}
            </Grid>
          </CardContent>
        </MotionCard>
      )}

      {!!activeCombos.length && (
        <MotionCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          sx={{
            borderRadius: 5,
            mb: 4,
            border: "1px solid rgba(183,28,28,0.12)",
            boxShadow: "0 14px 36px rgba(122,14,14,0.10)",
            background: "linear-gradient(180deg, #ffffff 0%, #FFFAF3 100%)",
          }}
        >
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "50%",
                  display: "grid",
                  placeItems: "center",
                  background: brand.gradientAmber,
                }}
              >
                <RestaurantMenuIcon sx={{ color: brand.redDeep }} />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontFamily: "'Playfair Display', Georgia, serif",
                    fontWeight: 900,
                    fontSize: { xs: 24, md: 30 },
                    color: brand.charcoal,
                  }}
                >
                  Combo Meals
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Best-value combo offers active right now.
                </Typography>
              </Box>
            </Stack>
            <Grid container spacing={3}>
              {activeCombos.map((combo, index) => renderComboCard(combo, index))}
            </Grid>
          </CardContent>
        </MotionCard>
      )}

      <Dialog
        open={offerDialog.open}
        onClose={closeOfferDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 5,
            overflow: "hidden",
            background: "linear-gradient(180deg, #ffffff 0%, #FFFAF3 100%)",
          },
        }}
      >
        <Box sx={{ background: brand.gradientPrimary, color: "#fff", px: 3, py: 2.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography
                sx={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontWeight: 800,
                  fontSize: 24,
                  lineHeight: 1.1,
                }}
              >
                {offerDialog.type === "combo"
                  ? offerDialog.data?.name || "Add Combo"
                  : offerDialog.data?.title || "Add Special"}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9, letterSpacing: 1 }}>
                {offerDialog.type === "combo" ? "COMBO MEAL" : "DAILY SPECIAL"}
              </Typography>
            </Box>
            <IconButton onClick={closeOfferDialog} sx={{ color: "#fff" }}>
              <CloseRoundedIcon />
            </IconButton>
          </Stack>
        </Box>

        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={2.5}>
            <Typography variant="body2" color="text.secondary">
              {offerDialog.type === "combo"
                ? offerDialog.data?.description || "Add this combo meal to your cart."
                : offerDialog.data?.description ||
                  offerDialog.data?.item_details?.description ||
                  "Add this special item to your cart."}
            </Typography>

            {offerDialog.type === "combo" && !!offerDialog.data?.combo_items?.length && (
              <Card sx={{ borderRadius: 4, bgcolor: "#FFF8F0" }}>
                <CardContent>
                  <Typography sx={{ fontWeight: 900, mb: 1 }}>Included Items</Typography>
                  <Stack spacing={0.8}>
                    {offerDialog.data.combo_items.map((item) => (
                      <Typography
                        key={item.id || `${item.item_id}-${item.display_order}`}
                        variant="body2"
                      >
                        <b>{item.quantity}×</b> {item.item_name}
                        {item.variant_name ? ` (${item.variant_name})` : ""}
                        {item.is_free ? " · Free" : ""}
                      </Typography>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            )}

            <Box>
              <Typography variant="overline" sx={{ fontWeight: 800, color: brand.red, letterSpacing: 2 }}>
                Quantity
              </Typography>
              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{
                  mt: 0.5,
                  bgcolor: "#fff",
                  borderRadius: 999,
                  px: 1,
                  py: 0.5,
                  border: "1px solid rgba(183,28,28,0.2)",
                  width: "fit-content",
                }}
              >
                <IconButton
                  onClick={() =>
                    setOfferDialog((prev) => ({
                      ...prev,
                      quantity: Math.max(1, Number(prev.quantity || 1) - 1),
                    }))
                  }
                >
                  <RemoveRoundedIcon />
                </IconButton>
                <Typography sx={{ fontWeight: 800, fontSize: 20, minWidth: 32, textAlign: "center" }}>
                  {offerDialog.quantity}
                </Typography>
                <IconButton
                  onClick={() =>
                    setOfferDialog((prev) => ({
                      ...prev,
                      quantity: Number(prev.quantity || 1) + 1,
                    }))
                  }
                  sx={{
                    color: "#fff",
                    background: brand.gradientPrimary,
                    "&:hover": { background: brand.gradientPrimary, opacity: 0.9 },
                  }}
                >
                  <AddRoundedIcon />
                </IconButton>
              </Stack>
            </Box>

            <Box>
              <Typography variant="overline" sx={{ fontWeight: 800, color: brand.red, letterSpacing: 2 }}>
                Notes
              </Typography>
              <TextField
                placeholder={
                  offerDialog.type === "combo"
                    ? "No onions, less spicy..."
                    : "Less spicy, extra cheese..."
                }
                value={offerDialog.notes}
                onChange={(event) =>
                  setOfferDialog((prev) => ({ ...prev, notes: event.target.value }))
                }
                fullWidth
                multiline
                minRows={3}
                sx={{ mt: 0.5, "& .MuiOutlinedInput-root": { borderRadius: 3, bgcolor: "#fff" } }}
              />
            </Box>

            <Card sx={{ borderRadius: 4, background: brand.gradientHero, color: "#fff" }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="caption" sx={{ opacity: 0.85, letterSpacing: 2 }}>
                      TOTAL · {offerDialog.quantity}×
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: "'Playfair Display', Georgia, serif",
                        fontWeight: 900,
                        fontSize: 32,
                        lineHeight: 1,
                        mt: 0.5,
                      }}
                    >
                      {money(
                        (offerDialog.type === "combo"
                          ? Number(offerDialog.data?.calculated_prices?.[serviceType]?.final_price || 0)
                          : Number(getOfferPrice(offerDialog.data, serviceType) || 0)) *
                          Number(offerDialog.quantity || 1)
                      )}
                    </Typography>
                  </Box>
                  <ShoppingCartIcon sx={{ fontSize: 56, color: brand.amber, opacity: 0.85 }} />
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={closeOfferDialog}
            sx={{
              borderRadius: 999,
              fontWeight: 700,
              textTransform: "none",
              px: 3,
              borderColor: "rgba(183,28,28,0.3)",
              color: brand.red,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmOfferAddToCart}
            startIcon={<AddShoppingCartIcon />}
            sx={{
              borderRadius: 999,
              fontWeight: 800,
              textTransform: "none",
              px: 3,
              py: 1.1,
              background: brand.gradientPrimary,
              "&:hover": { background: brand.gradientPrimary },
            }}
          >
            Add to Cart
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MenuOfferSections;
